const ISO_COUNTRY_CODES = [
  "AF","AX","AL","DZ","AS","AD","AO","AI","AQ","AG","AR","AM","AW","AU","AT","AZ",
  "BS","BH","BD","BB","BY","BE","BZ","BJ","BM","BT","BO","BQ","BA","BW","BV","BR","IO","BN","BG","BF","BI",
  "CV","KH","CM","CA","KY","CF","TD","CL","CN","CX","CC","CO","KM","CG","CD","CK","CR","CI","HR","CU","CW","CY","CZ",
  "DK","DJ","DM","DO","EC","EG","SV","GQ","ER","EE","SZ","ET","FK","FO","FJ","FI","FR","GF","PF","TF",
  "GA","GM","GE","DE","GH","GI","GR","GL","GD","GP","GU","GT","GG","GN","GW","GY","HT","HM","VA","HN","HK","HU",
  "IS","IN","ID","IR","IQ","IE","IM","IL","IT","JM","JP","JE","JO","KZ","KE","KI","KP","KR","KW","KG",
  "LA","LV","LB","LS","LR","LY","LI","LT","LU","MO","MG","MW","MY","MV","ML","MT","MH","MQ","MR","MU","YT","MX","FM","MD","MC","MN","ME","MS","MA","MZ","MM",
  "NA","NR","NP","NL","NC","NZ","NI","NE","NG","NU","NF","MK","MP","NO","OM",
  "PK","PW","PS","PA","PG","PY","PE","PH","PN","PL","PT","PR","QA","RE","RO","RU","RW",
  "BL","SH","KN","LC","MF","PM","VC","WS","SM","ST","SA","SN","RS","SC","SL","SG","SX","SK","SI","SB","SO","ZA","GS","SS","ES","LK","SD","SR","SJ","SE","CH","SY",
  "TW","TJ","TZ","TH","TL","TG","TK","TO","TT","TN","TR","TM","TC","TV","UG","UA","AE","GB","US","UM","UY","UZ",
  "VU","VE","VN","VG","VI","WF","EH","YE","ZM","ZW"
];

const COUNTRY_CODE_ALIASES = {
  USA: "US",
  "U.S.": "US",
  "U.S.A.": "US",
  "UNITED STATES OF AMERICA": "US",
  UK: "GB",
  "UNITED KINGDOM": "GB",
  EL: "GR",
  CHINA: "CN",
  "MAINLAND CHINA": "CN",
  "HONG KONG": "HK",
  TAIWAN: "TW",
};

function buildCountryNameMap() {
  const map = new Map();
  if (typeof Intl === "undefined" || !Intl.DisplayNames) return map;
  const displayNames = new Intl.DisplayNames(["en"], { type: "region" });
  ISO_COUNTRY_CODES.forEach((code) => {
    const name = displayNames.of(code);
    if (name) map.set(name.trim().toUpperCase(), code);
  });
  return map;
}

const COUNTRY_NAME_TO_CODE = buildCountryNameMap();

export const COUNTRY_SCOPE_MODES = {
  ALL: "ALL",
  WHITELIST: "WHITELIST",
  BLACKLIST: "BLACKLIST",
};

export function getCountryOptions(locale = "en") {
  const displayNames = typeof Intl !== "undefined" && Intl.DisplayNames
    ? new Intl.DisplayNames([locale], { type: "region" })
    : null;

  return ISO_COUNTRY_CODES
    .map(code => ({
      value: code,
      label: displayNames ? displayNames.of(code) || code : code,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function normalizeCountryCode(value) {
  if (!value) return "";
  let code = "";
  if (typeof value === "string") {
    code = value;
  } else if (typeof value === "object") {
    code = value.country_code || value.countryCode || value.country || value.region || "";
  }
  code = String(code).trim().toUpperCase();
  code = COUNTRY_CODE_ALIASES[code] || COUNTRY_NAME_TO_CODE.get(code) || code;
  return ISO_COUNTRY_CODES.includes(code) ? code : "";
}

export function normalizeCountryList(values) {
  const list = Array.isArray(values) ? values : (values ? [values] : []);
  return [...new Set(list.map(normalizeCountryCode).filter(Boolean))];
}

export function normalizeCountryScope(source = {}) {
  const rawScope = source.country_scope || source.countryScope || source.country_availability || {};
  const allowList = normalizeCountryList(
    rawScope.whitelist ||
    rawScope.white_list ||
    rawScope.allowlist ||
    rawScope.allowed_countries ||
    source.country_whitelist ||
    source.country_white_list ||
    source.country_allowlist ||
    source.allowed_countries
  );
  const denyList = normalizeCountryList(
    rawScope.blacklist ||
    rawScope.black_list ||
    rawScope.blocklist ||
    rawScope.blocked_countries ||
    source.country_blacklist ||
    source.country_black_list ||
    source.country_blocklist ||
    source.blocked_countries
  );
  const scopedCountries = normalizeCountryList(
    rawScope.country_codes ||
    rawScope.countries ||
    source.country_codes ||
    source.country_scope_countries
  );

  let mode = String(
    rawScope.mode ||
    rawScope.type ||
    source.country_scope_mode ||
    source.country_availability_mode ||
    ""
  ).trim().toUpperCase();

  if (mode === "ALLOWLIST" || mode === "WHITE_LIST") mode = COUNTRY_SCOPE_MODES.WHITELIST;
  if (mode === "DENYLIST" || mode === "BLOCKLIST" || mode === "BLACK_LIST") mode = COUNTRY_SCOPE_MODES.BLACKLIST;
  if (!mode && allowList.length) mode = COUNTRY_SCOPE_MODES.WHITELIST;
  if (!mode && denyList.length) mode = COUNTRY_SCOPE_MODES.BLACKLIST;
  if (!mode) mode = COUNTRY_SCOPE_MODES.ALL;

  const countryCodes = mode === COUNTRY_SCOPE_MODES.WHITELIST
    ? (allowList.length ? allowList : scopedCountries)
    : mode === COUNTRY_SCOPE_MODES.BLACKLIST
      ? (denyList.length ? denyList : scopedCountries)
      : [];

  if (!countryCodes.length) return { mode: COUNTRY_SCOPE_MODES.ALL, country_codes: [] };
  return { mode, country_codes: countryCodes };
}

export function isCountryScopeRestricted(source = {}) {
  return normalizeCountryScope(source).mode !== COUNTRY_SCOPE_MODES.ALL;
}

export function isCountryScopeAvailable(source = {}, countryContext = {}) {
  const scope = normalizeCountryScope(source);
  if (scope.mode === COUNTRY_SCOPE_MODES.ALL) return true;

  const candidateCodes = normalizeCountryList([
    countryContext.selected_country,
    countryContext.selectedCountry,
    countryContext.selected_country_code,
    countryContext.selectedCountryCode,
    countryContext.ip_country,
    countryContext.ipCountry,
    countryContext.ip_country_code,
    countryContext.ipCountryCode,
  ]);

  if (!candidateCodes.length) return false;

  if (scope.mode === COUNTRY_SCOPE_MODES.WHITELIST) {
    return candidateCodes.every(code => scope.country_codes.includes(code));
  }
  if (scope.mode === COUNTRY_SCOPE_MODES.BLACKLIST) {
    return candidateCodes.every(code => !scope.country_codes.includes(code));
  }
  return true;
}

export function getCountryContextFromStore(store) {
  const userInfo = (store && store.state && store.state.user_info) || {};
  const state = (store && store.state) || {};
  const selectedCountry = normalizeCountryCode(
    state.selected_country ||
    state.selectedCountry ||
    userInfo.selected_country ||
    userInfo.selectedCountry ||
    userInfo.country_code ||
    userInfo.country ||
    localStorage.getItem("selected_country_code") ||
    localStorage.getItem("selectedCountryCode") ||
    localStorage.getItem("country_code")
  );
  const ipCountry = normalizeCountryCode(
    state.ip_country ||
    state.ipCountry ||
    userInfo.ip_country ||
    userInfo.ipCountry ||
    localStorage.getItem("ip_country_code") ||
    localStorage.getItem("ipCountryCode")
  );

  return {
    selected_country: selectedCountry,
    ip_country: ipCountry,
  };
}

export function withCountryContext(params = {}, store) {
  const context = getCountryContextFromStore(store);
  return {
    ...params,
    selected_country: context.selected_country,
    ip_country: context.ip_country,
  };
}
